import type { NativeSelectOption } from '@shared/ui/fields'
import type { Status as BookingStatus } from '@brighterly/lib-core-types/booking/enums'
import type { LessonActionModalContext } from './interfaces'
import type { LessonStatusReasons } from './lesson-status'

import { ReasonCategory } from '@brighterly/lib-core-types/booking/enums'

import { useListsStore } from '@shared/model'

import { useLessonUpdateApi } from '../../api/lessons/update.hook'
import {
  LESSON_CANCEL_STATUSES,
  buildLessonStatusOptions,
  buildReasonCategoryOptions,
  buildReasonOptions,
  getFirstReason,
} from './lesson-status'

interface ModalLessonCancelReturn {
  refStatusOptions: ComputedRef<NativeSelectOption[]>
  refStatus: Ref<BookingStatus>
  refCategoryOptions: ComputedRef<NativeSelectOption[]>
  refCategory: Ref<string | null>
  refReasonOptions: ComputedRef<NativeSelectOption[]>
  refReason: Ref<string | null>
  refIsSaving: Ref<boolean>
  setStatus: (option: NativeSelectOption) => void
  setCategory: (option: NativeSelectOption) => void
  setReason: (option: NativeSelectOption) => void
  close: () => void
  confirm: () => Promise<void>
}

/** Cancels the lesson given: a cancel status with its reason, starting from the lesson's own when it has one. The
 *  modal stays open under its loader until the page's follow-up is done, and closes at once on a refusal. */
export const useModalLessonCancel = (context: LessonActionModalContext): ModalLessonCancelReturn => {
  const route = useRoute()
  const listsStore = useListsStore()

  const { lesson } = context
  const reasons = computed<LessonStatusReasons>(() =>
    (listsStore.getLists?.booking.change_status_reasons ?? {}) as LessonStatusReasons)

  const isCanceled = LESSON_CANCEL_STATUSES.includes(lesson.status)
  const initialCategory = isCanceled && lesson.reason_category ? lesson.reason_category : ReasonCategory.Admin

  const refStatus = ref<BookingStatus>(isCanceled ? lesson.status : LESSON_CANCEL_STATUSES[0]!)
  const refCategory = ref<string | null>(initialCategory)
  const refReason = ref<string | null>(isCanceled && lesson.reason ? lesson.reason : getFirstReason(reasons.value, initialCategory))
  const refIsSaving = ref<boolean>(false)

  const refStatusOptions = computed<NativeSelectOption[]>(() =>
    buildLessonStatusOptions(listsStore.getLists?.booking.status ?? {}, LESSON_CANCEL_STATUSES, refStatus.value))

  const refCategoryOptions = computed<NativeSelectOption[]>(() =>
    buildReasonCategoryOptions(reasons.value, listsStore.getLists?.booking.reason_category ?? {}, refCategory.value))

  const refReasonOptions = computed<NativeSelectOption[]>(() =>
    buildReasonOptions(reasons.value, refCategory.value, refReason.value))

  const setStatus = (option: NativeSelectOption): void => {
    refStatus.value = option.value as BookingStatus
  }

  const setCategory = (option: NativeSelectOption): void => {
    refCategory.value = option.value ? String(option.value) : null
    refReason.value = getFirstReason(reasons.value, refCategory.value)
  }

  const setReason = (option: NativeSelectOption): void => {
    refReason.value = option.value ? String(option.value) : null
  }

  const close = (): void => {
    if (refIsSaving.value) return
    context.close()
  }

  const confirm = async (): Promise<void> => {
    if (refIsSaving.value) return

    refIsSaving.value = true

    const isCanceledNow = await useLessonUpdateApi({
      disciplineId: route.params.id as string,
      lessonId: lesson.id,
      payload: {
        status: refStatus.value,
        reason: refReason.value,
        reason_category: refCategory.value,
      },
      successTitle: 'Successfully canceled!',
      successDescription: 'Selected lesson was successfully canceled!',
    })

    // The page's follow-up runs before the modal closes, under the modal's loader; a refusal closes it at once.
    if (isCanceledNow) await context.afterSave()

    refIsSaving.value = false
    context.close()
  }

  return {
    refStatusOptions,
    refStatus,
    refCategoryOptions,
    refCategory,
    refReasonOptions,
    refReason,
    refIsSaving,
    setStatus,
    setCategory,
    setReason,
    close,
    confirm,
  }
}
